import React, { useEffect, useRef, useState } from "react";

import { Fa } from "composants/Icon";
import { ONLYOFFICE_URL } from "customGlobal";
import { fetchOnlyOfficeConfig } from "api/onlyoffice";
import { t } from "utils/i18n";

const ONLYOFFICE_SCRIPT_ID = "onlyoffice-api-script";

declare global {
  interface Window {
    DocsAPI?: {
      DocEditor: new (
        placeholderId: string,
        config: Record<string, unknown>
      ) => { destroyEditor: () => void };
    };
  }
}

function loadOnlyOfficeScript(baseUrl: string): Promise<void> {
  if (window.DocsAPI) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(ONLYOFFICE_SCRIPT_ID);
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () =>
        reject(new Error("Impossible de charger OnlyOffice"))
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = ONLYOFFICE_SCRIPT_ID;
    script.src = `${baseUrl.replace(/\/$/, "")}/web-apps/apps/api/documents/api.js`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Impossible de charger OnlyOffice"));
    document.body.appendChild(script);
  });
}

function buildEditorConfig(payload: {
  config: Record<string, unknown>;
  token?: string;
}): Record<string, unknown> {
  if (payload.token) {
    return { ...payload.config, token: payload.token };
  }

  return payload.config;
}

interface OnlyOfficeEditorProps {
  documentId: string;
  documentName?: string;
  onClose: () => void;
  height?: number | string;
}

const OnlyOfficeEditor: React.FC<OnlyOfficeEditorProps> = ({
  documentId,
  documentName,
  onClose,
  height = "calc(70vh - 40px)"
}) => {
  const editorRef = useRef<{ destroyEditor: () => void } | null>(null);
  const onCloseRef = useRef(onClose);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const placeholderId = `onlyoffice-editor-${documentId}`;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const baseUrl = ONLYOFFICE_URL();
    if (!baseUrl) {
      setError("OnlyOffice non configuré");
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        await loadOnlyOfficeScript(baseUrl);
        const { data } = await fetchOnlyOfficeConfig(documentId);

        if (cancelled) {
          return;
        }

        if (!window.DocsAPI) {
          throw new Error("API OnlyOffice indisponible");
        }

        editorRef.current?.destroyEditor();

        editorRef.current = new window.DocsAPI.DocEditor(placeholderId, {
          ...buildEditorConfig(data),
          width: "100%",
          height: "100%",
          events: {
            onRequestClose: () => onCloseRef.current()
          }
        });
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Erreur OnlyOffice");
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      editorRef.current?.destroyEditor();
      editorRef.current = null;
    };
  }, [documentId, placeholderId]);

  return (
    <div
      style={{
        height,
        display: "flex",
        flexDirection: "column",
        minHeight: 0
      }}
    >
      <div className="level is-mobile mb-2" style={{ flexShrink: 0 }}>
        <div className="level-left">
          <div className="level-item">
            <button
              type="button"
              className="button is-small"
              onClick={() => onCloseRef.current()}
              title={t("commun_retour", { defaultValue: "Retour" })}
            >
              <span className="icon is-small">
                <Fa icon="arrow-left" />
              </span>
              <span>{t("commun_retour", { defaultValue: "Retour" })}</span>
            </button>
          </div>
          {documentName && (
            <div className="level-item">
              <strong>{documentName}</strong>
            </div>
          )}
        </div>
      </div>
      <div style={{ flex: 1, position: "relative", minHeight: 0, background: "white" }}>
        {loading && (
          <div
            className="flex flex-col items-center justify-center"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              background: "white"
            }}
          >
            <Fa icon="spinner" spin size="3x" className="text-primary mb-4" />
          </div>
        )}
        {error && (
          <div className="notification is-danger" style={{ margin: "1rem" }}>
            {error}
          </div>
        )}
        <div id={placeholderId} style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  );
};

export default OnlyOfficeEditor;
